import { CONTACT_CARRIER_ID_SUFFIXES, CONTACT_CARRIER_NAME_ALIASES } from 'yggdrasil/src/contactCarrierFields'

// Every standard contact field is carried to the server by a hidden input in `#cfAR`, and
// `getContactForm` (submit.ts) resolves that input by an id built from the page input's own `name`.
// One carrier's id does not follow the rule: the Phone input renders `name="phone_number"`, but its
// carrier is `#cf_contact_phone` (`contact[phone_number]`). Without this map the lookup misses, the
// phone is treated as a custom attribute and posted as a bare top-level `phone_number` param.
// The only server code that reads that bare param is the `checkout_params` fallback in
// `ContactConcern#find_create_or_merge_contact`, so an opt-in that also carried an email still
// saved its phone. A phone-only opt-in never got that far: with no `contact[...]` key at all,
// `missing_contact_params?` short-circuited the request, the number landed in
// `form_submissions.data` and nowhere else, and `contacts.phone_number` stayed blank (ENG-7661).
// Checkout is unaffected because it serializes off `data-cf-form-field` instead.

//
// Only a known carrier suffix resolves to a carrier (ENG-8498). Any other name, including a case
// variant such as `Phone`, returns null and is posted as a custom attribute. Names that are already
// a suffix use the `cf_contact_<name>` id directly, so CF Classic pages still emitting `name="phone"`
// keep resolving to the same carrier they always have. Map and Set lookups rather than an object
// literal: `name` is customer data, and an object lookup of a custom field called `constructor` or
// `toString` would reach the prototype and build a selector that throws out of the whole submit.
const CARRIER_SUFFIXES = new Set(CONTACT_CARRIER_ID_SUFFIXES)

export function contactCarrierInputId(name: string): string | null {
  const suffix = CONTACT_CARRIER_NAME_ALIASES.get(name) ?? name
  return CARRIER_SUFFIXES.has(suffix) ? `cf_contact_${suffix}` : null
}

// A Custom Type renders its key as the input's `name`, so a Checkbox with Custom Type `phone`
// renders `name="phone"`, the same name a CF Classic Phone input emits. The name alone can't tell
// them apart; the element's markup can. Input/V1 and TextArea/V1 render `type="custom_type"` on the
// field itself. Checkbox/V1 renders it on its wrapper, and SelectBox/V1 renders
// `data-type="custom_type"` on its wrapper. The wrapper is only consulted for those two: an Input's
// wrapper `type` comes from a param that can lag behind the field's own type.
export function isCustomTypeFormItem(item: Element): boolean {
  if (item.getAttribute('type') === 'custom_type') return true
  const wrapper = item.closest('.elFormItemWrapper')
  if (!wrapper) return false
  if (item.getAttribute('type') === 'checkbox') return wrapper.getAttribute('type') === 'custom_type'
  if (item.tagName === 'SELECT') return wrapper.getAttribute('data-type') === 'custom_type'
  return false
}
